import React, { useState, useEffect } from 'react';
import Header from '../include/header';
import Footer from '../include/footer';
import LeftPanel from '../include/LeftPanel';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from '../../components/ui/toast';
import axios from 'axios';
import AuthUser from '../../AuthUser';
import Global from '../../APIConfig';
import './DeveloperPage.css';

/**
 * "API & Integration" — self-service External Booking API page for vendors.
 * Native route (/Developer) inside the customer portal; only reachable when
 * authenticated (App.js gates all app routes on the portal token).
 */
function DeveloperPage() {
    let navigate = useNavigate();
    const { headers1, logout, user } = AuthUser();
    const [keyData, setKeyData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        LoadKey();
    }, []);

    const handleSessionExpired = () => {
        logout();
        navigate('/');
        window.location.reload();
    };

    // The API middleware redirects unauthenticated calls to the login page,
    // so any non-JSON response means the session is no longer valid.
    const parseApi = (response) => {
        const data = response.data;
        if (typeof data !== 'object' || data === null) {
            handleSessionExpired();
            return null;
        }
        if (data.status === 401) {
            handleSessionExpired();
            return null;
        }
        return data;
    };

    const LoadKey = () => {
        axios({
            method: 'post',
            url: Global.baseUrl+'getMyApiKey',
            headers: headers1,
            data: {}
        })
            .then(function (response) {
                const data = parseApi(response);
                if (!data) return;
                setLoaded(true);
                if (data.value && data.value.api_key) {
                    setKeyData(data.value);
                }
            })
            .catch(function () {
                toast.error('Could not load your API key. Please try again.');
                setLoaded(true);
            });
    };

    const GenerateKey = () => {
        setBusy(true);
        axios({
            method: 'post',
            url: Global.baseUrl+'generateApiKey',
            headers: headers1,
            data: {}
        })
            .then(function (response) {
                const data = parseApi(response);
                if (!data) return;
                if (data.value && data.value.api_key) {
                    setKeyData(data.value);
                    toast.success('API key generated.');
                } else {
                    toast.error(data.message || 'Could not generate a key.');
                }
            })
            .catch(function () {
                toast.error('Could not generate a key. Please try again.');
            })
            .then(function () {
                setBusy(false);
            });
    };

    const CopyKey = () => {
        if (keyData && keyData.api_key && navigator.clipboard) {
            navigator.clipboard.writeText(keyData.api_key);
            toast.success('API key copied to clipboard.');
        }
    };

    return (
        <>
            <LeftPanel/>
            <main className="px-main">
            <Header/>
            <ToastContainer/>
            <div className="px-content">
                <div className='col-12 col-md-12'>
                    <h2 className="px-pagehead__title mb-4">API &amp; Integration</h2>
                    <div className="developer-page">
                        {/* 1 · API key */}
                        <div className="card shadow-sm pxs-card">
                            <div className="pxs-card-title">1 · Your API key</div>
                            <p className="text-muted">
                                Your key links this account to the Pram Express booking API. The <b>WooCommerce plugin</b> uses it
                                to show live rates, book shipments, and track them. Keep it secret — anyone with the key can
                                book on your account{user && user.company ? ` (${user.company})` : ''}.
                            </p>
                            {!loaded ? (
                                <div className="text-muted">Checking your account…</div>
                            ) : keyData && keyData.api_key ? (
                                <div>
                                    <div className="pxs-key-box">
                                        <input type="text" className="form-control" readOnly value={keyData.api_key} />
                                        <button className="btn btn-primary pxs-btn" type="button" onClick={CopyKey}>Copy</button>
                                    </div>
                                    <div className="pxs-ok">
                                        ✔ Active{keyData.entry_date ? ` · issued ${keyData.entry_date}` : ''}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <p className="text-muted">No API key issued for this account yet.</p>
                                    <button className="btn btn-primary pxs-btn" type="button" disabled={busy} onClick={GenerateKey}>
                                        {busy ? 'Generating…' : 'Generate API key'}
                                    </button>
                                </div>
                            )}
                            <div className="pxs-note">
                                No active key yet? Click <b>Generate API key</b> — one key per account. Revoke it anytime via
                                Pram Express support.
                            </div>
                        </div>

                        {/* 2 · The endpoint */}
                        <div className="card shadow-sm pxs-card">
                            <div className="pxs-card-title">2 · The endpoint</div>
                            <p className="text-muted">
                                Base URL: <code>https://pramexpress.com/api/external/v1</code>
                            </p>
                            <table className="table table-striped pxs-table">
                                <thead>
                                    <tr>
                                        <th>Method</th>
                                        <th>Path</th>
                                        <th>Purpose</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td><code>GET</code></td><td><code>/services</code></td><td>Available services</td></tr>
                                    <tr><td><code>POST</code></td><td><code>/quotes</code></td><td>Live delivery price</td></tr>
                                    <tr><td><code>POST</code></td><td><code>/shipments</code></td><td>Book a shipment → AWB</td></tr>
                                    <tr><td><code>GET</code></td><td><code>/shipments/&#123;awb&#125;</code></td><td>Track a shipment</td></tr>
                                    <tr><td><code>POST</code></td><td><code>/shipments/&#123;awb&#125;/cancel</code></td><td>Cancel a shipment</td></tr>
                                </tbody>
                            </table>
                            <p className="text-muted">Authenticate every request with your key:</p>
                            <pre className="pxs-pre"><code>Authorization: Bearer {keyData ? keyData.api_key : 'px_live_…'}</code></pre>
                        </div>

                        {/* 3 · Plugin download */}
                        <div className="card shadow-sm pxs-card">
                            <div className="pxs-card-title">3 · Download the WooCommerce plugin</div>
                            <p className="text-muted">
                                Live rates at checkout, auto-booking (prepaid + COD), tracking, and a customer track page —
                                built for Ghana.
                            </p>
                            <a className="btn btn-dark pxs-dl" href="/downloads/pramexpress-shipping.zip" download>
                                ⬇ Download pramexpress-shipping.zip
                            </a>
                            <div className="pxs-note">Requires WooCommerce and your API key.</div>
                        </div>

                        {/* 4 · WordPress setup guide */}
                        <div className="card shadow-sm pxs-card">
                            <div className="pxs-card-title">4 · WordPress setup guide</div>
                            <ol className="pxs-steps">
                                <li><b>Install the plugin.</b> In WordPress: <b>Plugins → Add New → Upload Plugin</b> → choose <code>pramexpress-shipping.zip</code> → Install → Activate.</li>
                                <li><b>Open the wizard.</b> A <b>Pram Express</b> menu appears in the admin sidebar → the setup wizard starts. Paste your API key in step 2 and click <b>Test connection</b> (it should say “Connected”).</li>
                                <li><b>Pickup location.</b> Confirm your pickup country/city — Ghana / Accra is pre-selected. Add your shipper name, phone and email (these appear on the waybill).</li>
                                <li><b>Defaults.</b> Leave product type <b>Parcel</b>, parcel size 10×10×10 cm (edit if your parcels differ), payment type and billing as preferred.</li>
                                <li><b>Enable booking &amp; tracking.</b> Turn on <b>Auto-book on payment</b> (books prepaid automatically; COD books when an order moves to <i>Processing</i>) and <b>Status sync</b> + <b>Tracking link in emails</b>.</li>
                                <li><b>Save settings</b> — and don’t worry if the API-key field looks blank: <b>leave it blank to keep your saved key</b>.</li>
                                <li><b>Assign to a shipping zone.</b> WooCommerce → <b>Settings → Shipping → Zones</b> → add <b>Pram Express</b> to your Ghana zone (and set a fallback rate if you like).</li>
                                <li><b>Test.</b> Place a test order to a serviceable city — you’ll see live Pram Express rates at checkout, and the AWB appears on the order + the Pram Express dashboard.</li>
                            </ol>
                            <div className="pxs-note">
                                Base URL note: on production the plugin uses <code>https://pramexpress.com/api/external/v1</code> by default — no change needed.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer/>
            </main>
        </>
    );
}

export default DeveloperPage;
